import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star, X } from 'lucide-react';
import { useTechStack } from '@/hooks/useTechStack';
import { useUserReviews } from '@/hooks/useUserReviews';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ReviewForm } from '@/components/reviews/ReviewForm';
import { useState } from 'react';
import { optimizeImageUrl } from '@/lib/imageUtils';

/**
 * Quick Review Prompt Component
 * Shows a card prompting user to review a tool from their tech stack
 * Only shows if user has tools but hasn't reviewed them yet
 */
export function QuickReviewPrompt() {
  const { user } = useAuth();
  const { data: techStack } = useTechStack();
  const { data: reviews } = useUserReviews(user?.id);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  if (!user || !techStack || techStack.length === 0 || dismissed) {
    return null;
  }

  // Find products in tech stack that user hasn't reviewed yet
  const reviewedProductIds = new Set(reviews?.map(r => r.product_id) || []);
  const unreviewed = techStack.filter(
    item => item.product && !reviewedProductIds.has(item.product.id)
  );

  if (unreviewed.length === 0) {
    return null; // User has reviewed all their tools
  }

  // Pick the first unreviewed tool
  const promptProduct = unreviewed[0].product!;

  return (
    <>
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            {promptProduct.image && (
              <img
                src={optimizeImageUrl(promptProduct.image, { width: 80 })}
                alt={promptProduct.title}
                className="w-16 h-16 rounded object-cover flex-shrink-0"
                loading="lazy"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium mb-1">
                You use {promptProduct.title}. Help other agents by rating it.
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                Quick reviews help agents like you make better decisions
              </p>
              <Button
                size="sm"
                onClick={() => setSelectedProductId(promptProduct.id)}
                className="h-8"
              >
                <Star className="mr-1 h-3 w-3" />
                Rate {promptProduct.title}
              </Button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 flex-shrink-0"
              onClick={() => setDismissed(true)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog
        open={!!selectedProductId}
        onOpenChange={(open) => !open && setSelectedProductId(null)}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review {promptProduct.title}</DialogTitle>
          </DialogHeader>
          {selectedProductId && (
            <ReviewForm
              productId={selectedProductId}
              userId={user.id}
              onSuccess={() => {
                setSelectedProductId(null);
                setDismissed(true);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
